import { useEffect, useState, useRef } from "react";
import { PostCard } from "./PostCard";
import { PostRepository } from "@/infrastructure/repositories/PostRepository";
import { SharetribeRepository } from "@/infrastructure/repositories/SharetribeRepository";
import { apiService } from "@/services";
import { Button } from "../ui/button";
import { secureStorage } from "@/utils/secureStorage";

export function FeedList({
  currentUserId,
  reloadKey,
  onAddPost,
}: {
  currentUserId: string;
  reloadKey: number;
  onAddPost: () => void;
}) {
  const postRepo = new PostRepository();
  const userRepo = new SharetribeRepository(apiService);

  const [feeds, setFeeds] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const bottomRef = useRef<HTMLDivElement | null>(null);

  // DELETE DIALOG STATES
//  const [selectedPostId, setSelectedPostId] = useState<number | null>(null);

  // --------------------------------
  // Load Feeds
  // --------------------------------
  const loadFeeds = async (pageNo: number) => {
    try {
      setLoading(true);

      const uid = Number(localStorage.getItem("current_user_swimmer_id")) || 0;
      const coachId = Number(localStorage.getItem("coach_id")) || 0;

      if (pageNo === 1) {
        setFeeds([]);
        setHasMore(true);
      }

      const rawFeeds = await postRepo.fetchFeeds(uid, coachId, pageNo);

      if (!rawFeeds || rawFeeds.length === 0) {
        setHasMore(false);
        return;
      }

      const temp = rawFeeds.map((feed) => ({
        ...feed,
        userProfile: null,
        comments: [],
      }));

      setFeeds((prev) => {
        const merged = [...prev, ...temp];
        const unique = Array.from(new Map(merged.map((p) => [p.id, p])).values());
        return unique;
      });

      setTimeout(() => loadUserProfiles(rawFeeds), 0);
    } catch (err) {
      console.error("Feed load error:", err);
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------
  // Handle Delete (Open dialog)
  // --------------------------------
  const handleDeletePost = async (postId: number) => {
  const prevFeeds = feeds;

  setFeeds((f) => f.filter((p) => p.id !== postId));

  try {
    const userId =
      Number(localStorage.getItem("current_user_swimmer_id")) || 0;

    const status = await postRepo.deleteFeed(userId, postId);

    if (status !== 1) {
      setFeeds(prevFeeds);
    }
  } catch {
    setFeeds(prevFeeds);
  }
};



  // --------------------------------
  // User profile loader
  // --------------------------------
  const userCache: Record<string, any> = {};

  const loadUserProfiles = async (feedsPage: any[]) => {
    const uniqueUserIds = [...new Set(feedsPage.map((f) => String(f.user_id)))];

    for (const uid of uniqueUserIds) {
      if (userCache[uid]) {
        setFeeds((prev) =>
          prev.map((p) =>
            String(p.user_id) === uid ? { ...p, userProfile: userCache[uid] } : p
          )
        );
        continue;
      }

      const profile =
        (await userRepo.getUserById(Number(uid))) || {
          fname: "Unknown",
          lname: "",
          image: "/placeholder-avatar.jpg",
        };

      userCache[uid] = profile;

      setFeeds((prev) =>
        prev.map((p) =>
          String(p.user_id) === uid ? { ...p, userProfile: profile } : p
        )
      );
    }
  };

  // --------------------------------
  // Initial Load
  // --------------------------------
  useEffect(() => {
    loadFeeds(1);
  }, []);

  // reload feed
  useEffect(() => {
    setPage(1);
    loadFeeds(1);
  }, [reloadKey]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && hasMore) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );

    if (bottomRef.current) observer.observe(bottomRef.current);
    return () => observer.disconnect();
  }, [loading, hasMore]);

  useEffect(() => {
    if (page > 1) loadFeeds(page);
  }, [page]);


 const handleDownloadPractice = async (feedPracticeId: number) => {
  try {
    const postRepo = new PostRepository();

    const { practice, laps } = await postRepo.downloadFeedPractice(feedPracticeId);

    if (!practice) {
      console.error("Practice not found");
      return;
    }

    const html = `
      <html>
        <head>
          <title>Practice PDF</title>
          <style>
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              border: 1px solid #000;
              padding: 6px;
              text-align: left;
            }
            h1, h2 {
              margin-bottom: 10px;
            }
          </style>
        </head>
        <body>
          <h1>Practice Summary</h1>

          <p><strong>Date:</strong> ${practice.practice_date}</p>
          <p><strong>Time:</strong> ${practice.practice_time}</p>

          <h2>Practice Sets</h2>
          <table>
            <thead>
              <tr>
                <th>Set</th>
                <th>Reps</th>
                <th>Distance</th>
                <th>Interval</th>
                <th>Energy Zone</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              ${laps
                .map(
                  (lap: any, index: number) => `
                <tr>
                  <td>Set ${index + 1}</td>
                  <td>${lap.rep}</td>
                  <td>${lap.distance}</td>
                  <td>${Math.floor(lap.interval / 60)}:${(lap.interval % 60)
                    .toString()
                    .padStart(2, "0")}</td>
                  <td>${lap.energy_zone}</td>
                  <td>${lap.description || ""}</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([html], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `Practice_${feedPracticeId}.pdf`;
    link.click();

    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Download practice failed:", err);
  }
};

  return (
    <div className="space-y-4">

      {/* Skeleton first page */}
      {loading && feeds.length === 0 && (
        <div className="fixed inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      )}


      <div className="flex justify-end">
        <Button  onClick={onAddPost}>
          + Add Posts
        </Button>
      </div>

      {/* No posts */}
      {!loading && feeds.length === 0 && !hasMore && (
        <p className="text-center text-gray-500 py-6">No posts yet.</p>
      )}

      {/* Posts */}
      {feeds.map((feed) => (
        <PostCard
          key={feed.id}
          post={{
            ...feed,
            user:
              feed.user_id == currentUserId
                ? secureStorage.get("current_user_name") || "You"
                : `${feed.userProfile?.fname || ""} ${feed.userProfile?.lname || ""}`.trim() ||
                  "Unknown",
            userPic:
              feed.user_id == currentUserId
                ? secureStorage.get("current_user_pic") ||
                  "/placeholder-avatar.jpg"
                : feed.userProfile?.image || "/placeholder-avatar.jpg",
            photo: feed.photo,
            video: feed.video,
          }}
          onDelete={handleDeletePost}
          currentUserId={currentUserId}
           onDownloadPractice={handleDownloadPractice}
        />
      ))}

      {loading && feeds.length > 0 && (
        <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
        </div>
      )}

      <div ref={bottomRef} className="h-1"></div>

    </div>
  );
}
